import React, {useEffect, useState} from 'react';
import {Card, Col, ConfigProvider, DatePicker, Row, Spin, Statistic, Tabs} from 'antd';
import dayjs from 'dayjs';
import 'dayjs/locale/ru';
import customParseFormat from 'dayjs/plugin/customParseFormat';
import ruRU from 'antd/locale/ru_RU';
import ExpenseLineChart from './components/ExpenseLineChart';
import ExpenseMonthlyCards from './components/ExpenseMonthlyCards';
import ExpensePieChart from './components/ExpensePieChart';
import IncomePieChart from './components/IncomePieChart';
import {instance} from "../../API/API";
import {ArrowDownOutlined, ArrowUpOutlined} from '@ant-design/icons';

dayjs.locale('ru');
dayjs.extend(customParseFormat);

const {RangePicker} = DatePicker;

const Dashboard = () => {
    const [dateRange, setDateRange] = useState({
        start: '2026-01-01',
        end: '2026-12-31'
    });
    const [expenseData, setExpenseData] = useState(null);
    const [incomeData, setIncomeData] = useState({
        periodic: null,
        oneTime: null,
        total: null
    });
    const [loading, setLoading] = useState(false);
    const [incomeLoading, setIncomeLoading] = useState(false);

    // Получение данных о расходах
    const fetchExpenseData = async () => {
        setLoading(true);
        try {
            const response = await instance.get('finance_04122025/get_expense_analytics.php', {
                params: {
                    date_from: dateRange.start,
                    date_to: dateRange.end
                }
            });
            if (response.data.success) {
                console.log('Expense data loaded:', response.data.data);
                setExpenseData(response.data.data);
            }
        } catch (error) {
            console.error('Error fetching expense data:', error);
        } finally {
            setLoading(false);
        }
    };

    // Получение данных о доходах (оба типа)
    const fetchIncomeData = async () => {
        setIncomeLoading(true);
        try {
            // Параметры для обоих запросов
            const params = {
                'api-key': 'null',
                date_from: dateRange.start,
                date_to: dateRange.end
            };

            // Получаем периодические доходы (ВСЕ типы) из getPayment.php
            // type 1 - наличные, type 2 - безналичные
            const periodicPromise = instance.get('finance/getPayment.php', {
                params // УБИРАЕМ фильтрацию по type!
            });

            // Получаем разовые доходы из getOneIncome.php
            const oneTimePromise = instance.get('finance/getOneIncome.php', {
                params
            });

            // Выполняем оба запроса параллельно
            const [periodicResponse, oneTimeResponse] = await Promise.all([
                periodicPromise,
                oneTimePromise
            ]);

            let periodicData = [];
            let oneTimeData = [];

            // Обрабатываем периодические доходы
            if (periodicResponse.data.access) {
                periodicData = periodicResponse.data.payments || [];
                console.log('Periodic incomes from getPayment.php:', periodicData);

                // Фильтруем только фактические доходы (исключаем type 0)
                periodicData = periodicData.filter(payment =>
                    payment.type !== "0" && // исключаем планируемые
                    payment.cost &&
                    parseFloat(payment.cost) > 0
                );
            }

            // Обрабатываем разовые доходы
            if (oneTimeResponse.data.access) {
                oneTimeData = oneTimeResponse.data.one_income || [];
                console.log('One-time incomes from getOneIncome.php:', oneTimeData);

                // Фильтруем только валидные записи
                oneTimeData = oneTimeData.filter(income =>
                    income.date &&
                    income.cost &&
                    parseFloat(income.cost) > 0 &&
                    parseInt(income.date) > 0 &&
                    income.type !== "0" // исключаем тип 0
                );
            }

            // Обрабатываем данные
            const processedPeriodic = processIncomeData(periodicData, 'periodic');
            const processedOneTime = processIncomeData(oneTimeData, 'oneTime');

            // Объединяем данные
            const totalIncome = processedPeriodic.total + processedOneTime.total;
            const totalCash = processedPeriodic.cash + processedOneTime.cash;
            const totalNonCash = processedPeriodic.nonCash + processedOneTime.nonCash;

            // Объединяем месячные данные
            const monthlyMap = {};

            [...processedPeriodic.monthly, ...processedOneTime.monthly].forEach(month => {
                if (!monthlyMap[month.monthKey]) {
                    monthlyMap[month.monthKey] = {...month};
                } else {
                    monthlyMap[month.monthKey].total += month.total;
                    monthlyMap[month.monthKey].cash += month.cash;
                    monthlyMap[month.monthKey].nonCash += month.nonCash;
                }
            });

            const monthlyData = Object.values(monthlyMap)
                .sort((a, b) => a.monthKey.localeCompare(b.monthKey));

            setIncomeData({
                periodic: processedPeriodic,
                oneTime: processedOneTime,
                total: {
                    total: totalIncome,
                    cash: totalCash,
                    nonCash: totalNonCash,
                    monthly: monthlyData,
                    byType: {
                        periodic: processedPeriodic.total,
                        oneTime: processedOneTime.total
                    }
                }
            });

        } catch (error) {
            console.error('Error fetching income data:', error);
            setIncomeData({
                periodic: null,
                oneTime: null,
                total: null
            });
        } finally {
            setIncomeLoading(false);
        }
    };

    // Обработка данных о доходах
    const processIncomeData = (incomes, type) => {
        if (!incomes || incomes.length === 0) {
            return {
                total: 0,
                monthly: [],
                cash: 0,
                nonCash: 0,
                type: type
            };
        }

        let totalIncome = 0;
        let totalCash = 0;
        let totalNonCash = 0;

        const monthlyMap = {};

        incomes.forEach(income => {
            const amount = parseFloat(income.cost) || 0;

            // Пропускаем записи с нулевой суммой
            if (amount === 0) return;

            let date;
            try {
                const dateValue = parseInt(income.date);
                if (dateValue > 0) {
                    date = dayjs.unix(dateValue);
                } else {
                    console.warn('Invalid date for income:', income);
                    return;
                }
            } catch (error) {
                console.warn('Error parsing date for income:', income, error);
                return;
            }

            const monthKey = date.format('YYYY-MM');

            let isCash = false;
            const nds = parseInt(income.nds);
            const paymentType = parseInt(income.type);

            if (paymentType === 1) {
                isCash = true; // наличные
            } else if (paymentType === 2) {
                isCash = false; // безналичные
            } else {
                console.warn('Unknown payment type for periodic income:', income);
                return;
            }

            totalIncome += amount;

            type === 'periodic' && console.log('total: ' + totalIncome);
            if (isCash) {
                totalCash += amount;
                type === 'periodic' && console.log('cash: ' + totalCash);
            } else {
                totalNonCash += amount;
                type === 'periodic' && console.log('totalNonCash: ' + totalNonCash);
            }

            // Группировка по месяцам
            if (!monthlyMap[monthKey]) {
                monthlyMap[monthKey] = {
                    month: date.format('MMMM YYYY'),
                    monthKey: monthKey,
                    total: 0,
                    cash: 0,
                    nonCash: 0
                };
            }

            monthlyMap[monthKey].total += amount;

            if (isCash) {
                monthlyMap[monthKey].cash += amount;
            } else {
                monthlyMap[monthKey].nonCash += amount;
            }
        });

        // Преобразуем в массив и сортируем
        const monthlyData = Object.values(monthlyMap)
            .sort((a, b) => a.monthKey.localeCompare(b.monthKey));

        return {
            total: parseFloat(totalIncome.toFixed(2)),
            cash: parseFloat(totalCash.toFixed(2)),
            nonCash: parseFloat(totalNonCash.toFixed(2)),
            monthly: monthlyData,
            type: type,
            rawData: incomes
        };
    };

    useEffect(() => {
        fetchExpenseData();
        fetchIncomeData();
    }, [dateRange]);

    const handleDateChange = (dates) => {
        if (dates && dates[0] && dates[1]) {
            const format = 'YYYY-MM-DD';
            setDateRange({
                start: dates[0].format(format),
                end: dates[1].format(format)
            });
        }
    };

    // Подготавливаем данные для круговой диаграммы расходов
    const getExpensePieChartData = () => {
        if (!expenseData) return [];

        const total = expenseData.stats.total_expenses;
        if (total === 0) return [];

        return [
            {
                name: 'Зарплата',
                value: expenseData.stats.total_salary,
                color: '#1890ff',
                percentage: ((expenseData.stats.total_salary / total) * 100).toFixed(1)
            },
            {
                name: 'Периодические',
                value: expenseData.stats.total_periodic,
                color: '#52c41a',
                percentage: ((expenseData.stats.total_periodic / total) * 100).toFixed(1)
            },
            {
                name: 'Разовые',
                value: expenseData.stats.total_one_time,
                color: '#fa8c16',
                percentage: ((expenseData.stats.total_one_time / total) * 100).toFixed(1)
            }
        ];
    };

    // Подготавливаем данные для круговой диаграммы доходов
    const getIncomePieChartData = () => {
        if (!incomeData.total || incomeData.total.total === 0) return [];

        return [
            {
                name: 'Периодические',
                value: incomeData.total.byType.periodic,
                color: '#52c41a',
                percentage: ((incomeData.total.byType.periodic / incomeData.total.total) * 100).toFixed(1)
            },
            {
                name: 'Разовые',
                value: incomeData.total.byType.oneTime,
                color: '#fa8c16',
                percentage: ((incomeData.total.byType.oneTime / incomeData.total.total) * 100).toFixed(1)
            }
        ];
    };

    // Рассчитываем баланс
    const calculateBalance = () => {
        if (!expenseData || !incomeData.total) return null;

        const totalExpenses = expenseData.stats.total_expenses;
        const totalIncome = incomeData.total.total;
        const balance = totalIncome - totalExpenses;
        const percentage = totalIncome > 0 ? (balance / totalIncome) * 100 : 0;

        return {
            totalIncome,
            totalExpenses,
            balance,
            percentage,
            isPositive: balance >= 0
        };
    };

    const balanceData = calculateBalance();

    return (
        <ConfigProvider locale={ruRU}>
            <div style={{padding: '20px'}}>
                <h1>Финансовый дашборд</h1>

                {/* Выбор периода */}
                <Card style={{marginBottom: '20px'}}>
                    <Row gutter={16} align="middle">
                        <Col>
                            <strong>Период:</strong>
                        </Col>
                        <Col>
                            <RangePicker
                                value={[
                                    dayjs(dateRange.start),
                                    dayjs(dateRange.end)
                                ]}
                                onChange={handleDateChange}
                                format="DD.MM.YYYY"
                                style={{width: '250px'}}
                                placeholder={['Начало', 'Конец']}
                            />
                        </Col>
                    </Row>
                </Card>

                {/* Карточка с балансом */}
                {balanceData && (
                    <Card
                        style={{
                            marginBottom: '20px',
                            background: balanceData.isPositive ? '#f6ffed' : '#fff2f0',
                            borderColor: balanceData.isPositive ? '#b7eb8f' : '#ffccc7'
                        }}
                    >
                        <Row gutter={24}>
                            <Col span={8}>
                                <Statistic
                                    title="Общий доход"
                                    value={balanceData.totalIncome}
                                    precision={2}
                                    valueStyle={{color: '#52c41a', fontWeight: 'bold'}}
                                    prefix="€"
                                />
                            </Col>
                            <Col span={8}>
                                <Statistic
                                    title="Общие расходы"
                                    value={balanceData.totalExpenses}
                                    precision={2}
                                    valueStyle={{color: '#ff4d4f', fontWeight: 'bold'}}
                                    prefix="€"
                                />
                            </Col>
                            <Col span={8}>
                                <Statistic
                                    title="Баланс"
                                    value={balanceData.balance}
                                    precision={2}
                                    valueStyle={{
                                        color: balanceData.isPositive ? '#52c41a' : '#ff4d4f',
                                        fontWeight: 'bold',
                                        fontSize: '24px'
                                    }}
                                    prefix={balanceData.isPositive ? <ArrowUpOutlined/> : <ArrowDownOutlined/>}
                                    suffix="€"
                                />
                                <div style={{marginTop: '8px', fontSize: '14px', color: '#666'}}>
                                    {balanceData.isPositive ? '+' : ''}{balanceData.percentage.toFixed(1)}% от дохода
                                </div>
                            </Col>
                        </Row>
                    </Card>
                )}

                {loading || incomeLoading ? (
                    <Spin size="large" style={{display: 'block', margin: '50px auto'}}/>
                ) : (
                    <>
                        {/* Секция доходов */}
                        {incomeData.total && incomeData.total.total > 0 && (
                            <>
                                {/* Общая карточка доходов */}
                                <Card
                                    title="Доходы"
                                    style={{marginBottom: '20px'}}
                                    headStyle={{
                                        background: '#f0f7ff',
                                        borderBottomColor: '#1890ff',
                                        fontWeight: 'bold',
                                        fontSize: '16px',
                                        color: '#262626'
                                    }}
                                >
                                    <Row gutter={24}>
                                        <Col span={12}>
                                            <div style={{padding: '10px'}}>
                                                <div style={{
                                                    fontSize: '28px',
                                                    fontWeight: 'bold',
                                                    color: '#52c41a',
                                                    marginBottom: '10px'
                                                }}>
                                                    {incomeData.total.total.toLocaleString('ru-RU', {minimumFractionDigits: 2})} €
                                                </div>

                                                <div style={{
                                                    background: '#fafafa',
                                                    padding: '15px',
                                                    borderRadius: '6px',
                                                    marginTop: '20px',
                                                    border: '1px solid #f0f0f0'
                                                }}>
                                                    <div style={{
                                                        fontSize: '16px',
                                                        fontWeight: 'bold',
                                                        marginBottom: '10px',
                                                        color: '#262626'
                                                    }}>
                                                        По типам оплаты
                                                    </div>
                                                    <Row gutter={16}>
                                                        <Col span={12}>
                                                            <div style={{
                                                                color: '#52c41a',
                                                                fontSize: '15px',
                                                                fontWeight: 'bold'
                                                            }}>
                                                                Наличные
                                                            </div>
                                                            <div style={{fontSize: '18px', marginTop: '5px'}}>
                                                                {incomeData.total.cash.toLocaleString('ru-RU', {minimumFractionDigits: 2})} €
                                                            </div>
                                                        </Col>
                                                        <Col span={12}>
                                                            <div style={{
                                                                color: '#722ed1',
                                                                fontSize: '15px',
                                                                fontWeight: 'bold'
                                                            }}>
                                                                Безналичные
                                                            </div>
                                                            <div style={{fontSize: '18px', marginTop: '5px'}}>
                                                                {incomeData.total.nonCash.toLocaleString('ru-RU', {minimumFractionDigits: 2})} €
                                                            </div>
                                                        </Col>
                                                    </Row>
                                                </div>
                                            </div>
                                        </Col>
                                        <Col span={12}>
                                            <IncomePieChart data={getIncomePieChartData()}/>
                                        </Col>
                                    </Row>
                                </Card>

                                {/* Две карточки с деталями доходов */}
                                <Row gutter={16} style={{marginBottom: '20px'}}>
                                    {/* Периодические доходы */}
                                    <Col span={12}>
                                        <Card
                                            title="Периодические доходы"
                                            style={{height: '100%'}}
                                            headStyle={{
                                                background: '#f6ffed',
                                                borderBottomColor: '#52c41a',
                                                fontWeight: 'bold',
                                                fontSize: '15px',
                                                color: '#262626'
                                            }}
                                        >
                                            <div style={{
                                                fontSize: '32px',
                                                fontWeight: 'bold',
                                                color: '#52c41a',
                                                marginBottom: '15px'
                                            }}>
                                                {incomeData.periodic?.total.toLocaleString('ru-RU', {minimumFractionDigits: 2}) || '0.00'} €
                                            </div>

                                            <div style={{marginTop: '20px'}}>
                                                <div style={{
                                                    background: '#fafafa',
                                                    padding: '12px',
                                                    borderRadius: '6px',
                                                    marginTop: '15px',
                                                    border: '1px solid #f0f0f0'
                                                }}>
                                                    <div style={{
                                                        fontSize: '14px',
                                                        fontWeight: 'bold',
                                                        marginBottom: '8px',
                                                        color: '#262626'
                                                    }}>
                                                        Распределение по оплате
                                                    </div>
                                                    <div style={{fontSize: '13px'}}>
                                                        <div style={{color: '#52c41a', marginBottom: '4px'}}>
                                                            Наличные: {incomeData.periodic?.cash.toLocaleString('ru-RU', {minimumFractionDigits: 2}) || '0.00'} €
                                                        </div>
                                                        <div style={{color: '#722ed1'}}>
                                                            Безналичные: {incomeData.periodic?.nonCash.toLocaleString('ru-RU', {minimumFractionDigits: 2}) || '0.00'} €
                                                        </div>
                                                    </div>
                                                </div>
                                            </div>
                                        </Card>
                                    </Col>

                                    {/* Разовые доходы */}
                                    <Col span={12}>
                                        <Card
                                            title="Разовые доходы"
                                            style={{height: '100%'}}
                                            headStyle={{
                                                background: '#fff7e6',
                                                borderBottomColor: '#fa8c16',
                                                fontWeight: 'bold',
                                                fontSize: '15px',
                                                color: '#262626'
                                            }}
                                        >
                                            <div style={{
                                                fontSize: '32px',
                                                fontWeight: 'bold',
                                                color: '#fa8c16',
                                                marginBottom: '15px'
                                            }}>
                                                {incomeData.oneTime?.total.toLocaleString('ru-RU', {minimumFractionDigits: 2}) || '0.00'} €
                                            </div>

                                            <div style={{marginTop: '20px'}}>
                                                <div style={{
                                                    background: '#fafafa',
                                                    padding: '12px',
                                                    borderRadius: '6px',
                                                    marginTop: '15px',
                                                    border: '1px solid #f0f0f0'
                                                }}>
                                                    <div style={{
                                                        fontSize: '14px',
                                                        fontWeight: 'bold',
                                                        marginBottom: '8px',
                                                        color: '#262626'
                                                    }}>
                                                        Распределение по оплате
                                                    </div>
                                                    <div style={{fontSize: '13px'}}>
                                                        <div style={{color: '#52c41a', marginBottom: '4px'}}>
                                                            Наличные: {incomeData.oneTime?.cash.toLocaleString('ru-RU', {minimumFractionDigits: 2}) || '0.00'} €
                                                        </div>
                                                        <div style={{color: '#722ed1'}}>
                                                            Безналичные: {incomeData.oneTime?.nonCash.toLocaleString('ru-RU', {minimumFractionDigits: 2}) || '0.00'} €
                                                        </div>
                                                    </div>
                                                </div>
                                            </div>
                                        </Card>
                                    </Col>
                                </Row>
                            </>
                        )}

                        {/* Секция расходов */}
                        {expenseData && (
                            <>
                                <Card
                                    title="Расходы"
                                    style={{marginBottom: '20px'}}
                                    headStyle={{
                                        background: '#f9f0ff',
                                        borderBottomColor: '#d3adf7',
                                        fontWeight: 'bold',
                                        fontSize: '16px',
                                        color: '#262626'
                                    }}
                                >
                                    <Row gutter={24}>
                                        <Col span={12}>
                                            <div style={{padding: '10px'}}>
                                                <div style={{
                                                    fontSize: '28px',
                                                    fontWeight: 'bold',
                                                    color: '#cf1322',
                                                    marginBottom: '10px'
                                                }}>
                                                    {expenseData.stats.total_expenses.toLocaleString('ru-RU', {minimumFractionDigits: 2})} €
                                                </div>

                                                <div style={{marginBottom: '15px'}}>
                                                    <div style={{fontSize: '18px', color: '#666', marginBottom: '5px'}}>
                                                        Среднемесячные расходы
                                                    </div>
                                                    <div style={{
                                                        fontSize: '22px',
                                                        fontWeight: 'bold',
                                                        color: '#262626'
                                                    }}>
                                                        {expenseData.stats.avg_monthly.toLocaleString('ru-RU', {minimumFractionDigits: 2})} €
                                                    </div>
                                                </div>

                                                <div style={{
                                                    background: '#fafafa',
                                                    padding: '15px',
                                                    borderRadius: '6px',
                                                    marginTop: '20px',
                                                    border: '1px solid #f0f0f0'
                                                }}>
                                                    <div style={{
                                                        fontSize: '16px',
                                                        fontWeight: 'bold',
                                                        marginBottom: '10px',
                                                        color: '#262626'
                                                    }}>
                                                        По типам оплаты
                                                    </div>
                                                    <Row gutter={16}>
                                                        <Col span={12}>
                                                            <div style={{
                                                                color: '#52c41a',
                                                                fontSize: '15px',
                                                                fontWeight: 'bold'
                                                            }}>
                                                                Наличные
                                                            </div>
                                                            <div style={{fontSize: '18px', marginTop: '5px'}}>
                                                                {expenseData.stats.total_cash.toLocaleString('ru-RU', {minimumFractionDigits: 2})} €
                                                            </div>
                                                        </Col>
                                                        <Col span={12}>
                                                            <div style={{
                                                                color: '#722ed1',
                                                                fontSize: '15px',
                                                                fontWeight: 'bold'
                                                            }}>
                                                                Безналичные
                                                            </div>
                                                            <div style={{fontSize: '18px', marginTop: '5px'}}>
                                                                {expenseData.stats.total_non_cash.toLocaleString('ru-RU', {minimumFractionDigits: 2})} €
                                                            </div>
                                                        </Col>
                                                    </Row>
                                                </div>
                                            </div>
                                        </Col>
                                        <Col span={12}>
                                            <ExpensePieChart data={getExpensePieChartData()}/>
                                        </Col>
                                    </Row>
                                </Card>

                                {/* Три вертикальные карточки с деталями расходов */}
                                <Row gutter={16} style={{marginBottom: '20px'}}>
                                    {/* Зарплаты */}
                                    <Col span={8}>
                                        <Card
                                            title="Фонд заработной платы"
                                            style={{height: '100%'}}
                                            headStyle={{
                                                background: '#f0f7ff',
                                                borderBottomColor: '#1890ff',
                                                fontWeight: 'bold',
                                                fontSize: '15px',
                                                color: '#262626'
                                            }}
                                        >
                                            <div style={{
                                                fontSize: '32px',
                                                fontWeight: 'bold',
                                                color: '#1890ff',
                                                marginBottom: '15px'
                                            }}>
                                                {expenseData.stats.total_salary.toLocaleString('ru-RU', {minimumFractionDigits: 2})} €
                                            </div>

                                            <div style={{marginTop: '20px'}}>
                                                <div style={{fontSize: '14px', color: '#666', marginBottom: '8px'}}>
                                                    Среднемесячно: <strong>{expenseData.stats.salary_monthly_avg.toLocaleString('ru-RU', {minimumFractionDigits: 2})} €</strong>
                                                </div>
                                            </div>
                                        </Card>
                                    </Col>

                                    {/* Периодические расходы */}
                                    <Col span={8}>
                                        <Card
                                            title="Периодические расходы"
                                            style={{height: '100%'}}
                                            headStyle={{
                                                background: '#f6ffed',
                                                borderBottomColor: '#52c41a',
                                                fontWeight: 'bold',
                                                fontSize: '15px',
                                                color: '#262626'
                                            }}
                                        >
                                            <div style={{
                                                fontSize: '32px',
                                                fontWeight: 'bold',
                                                color: '#52c41a',
                                                marginBottom: '15px'
                                            }}>
                                                {expenseData.stats.total_periodic.toLocaleString('ru-RU', {minimumFractionDigits: 2})} €
                                            </div>

                                            <div style={{marginTop: '20px'}}>
                                                <div style={{fontSize: '14px', color: '#666', marginBottom: '8px'}}>
                                                    Среднемесячно: <strong>{expenseData.stats.periodic_monthly_avg.toLocaleString('ru-RU', {minimumFractionDigits: 2})} €</strong>
                                                </div>
                                            </div>
                                        </Card>
                                    </Col>

                                    {/* Разовые расходы */}
                                    <Col span={8}>
                                        <Card
                                            title="Разовые расходы"
                                            style={{height: '100%'}}
                                            headStyle={{
                                                background: '#fff7e6',
                                                borderBottomColor: '#fa8c16',
                                                fontWeight: 'bold',
                                                fontSize: '15px',
                                                color: '#262626'
                                            }}
                                        >
                                            <div style={{
                                                fontSize: '32px',
                                                fontWeight: 'bold',
                                                color: '#fa8c16',
                                                marginBottom: '15px'
                                            }}>
                                                {expenseData.stats.total_one_time.toLocaleString('ru-RU', {minimumFractionDigits: 2})} €
                                            </div>

                                            <div style={{marginTop: '20px'}}>
                                                <div style={{fontSize: '14px', color: '#666', marginBottom: '8px'}}>
                                                    Среднемесячно: <strong>{expenseData.stats.one_time_monthly_avg.toLocaleString('ru-RU', {minimumFractionDigits: 2})} €</strong>
                                                </div>
                                            </div>
                                        </Card>
                                    </Col>
                                </Row>

                                {/* Визуализация данных расходов */}
                                <Tabs
                                    defaultActiveKey="1"
                                    items={[
                                        {
                                            key: '1',
                                            label: 'График расходов',
                                            children: <ExpenseLineChart data={expenseData.monthly_expenses}/>
                                        },
                                        {
                                            key: '2',
                                            label: 'Карточки по месяцам',
                                            children: <ExpenseMonthlyCards data={expenseData.monthly_expenses}/>
                                        }
                                    ]}
                                />
                            </>
                        )}
                    </>
                )}
            </div>
        </ConfigProvider>
    );
};

export default Dashboard;
